import React, { useEffect, useState } from 'react';
import { triageService } from '../../services/diagnostic.service';

const TriagePanel = ({ schemeId, channel = 'AGENT', context = {}, onOutcome }) => {
    const [categories, setCategories] = useState([]);
    const [session, setSession] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!schemeId) return;
        triageService.categories(schemeId).then((rows) => setCategories(Array.isArray(rows) ? rows : [])).catch(() => setCategories([]));
    }, [schemeId]);

    const start = async (entryCategory) => {
        setBusy(true);
        setError('');
        try {
            const res = await triageService.start({
                schemeId: Number(schemeId),
                entryCategory,
                channel,
                context,
            });
            setSession(res);
            if (res.outcome && onOutcome) onOutcome(res);
        } catch (e) {
            setError(e.message || 'Could not start triage');
        } finally {
            setBusy(false);
        }
    };

    const answer = async (value) => {
        if (!session?.sessionId || !session?.node?.id) return;
        setBusy(true);
        setError('');
        try {
            const res = await triageService.answer(session.sessionId, session.node.id, value);
            setSession(res);
            if (res.outcome && onOutcome) onOutcome(res);
        } catch (e) {
            setError(e.message || 'Could not save answer');
        } finally {
            setBusy(false);
        }
    };

    const back = async () => {
        if (!session?.sessionId) return;
        setBusy(true);
        try {
            setSession(await triageService.back(session.sessionId));
        } catch (e) {
            setError(e.message || 'Could not go back');
        } finally {
            setBusy(false);
        }
    };

    const path = session?.path || [];

    return (
        <div className="border border-gray-200 rounded-md p-3 bg-white">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900">Triage</h3>
                {session?.sessionId && (
                    <button type="button" className="text-xs text-blue-700" onClick={back} disabled={busy}>
                        Back
                    </button>
                )}
            </div>
            {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
            {!session && (
                <div className="flex flex-wrap gap-2">
                    {categories.length === 0 && (
                        <p className="text-xs text-gray-500">No published diagnostic flows for this scheme.</p>
                    )}
                    {categories.map((c) => (
                        <button
                            key={c.code}
                            type="button"
                            disabled={busy || !schemeId}
                            onClick={() => start(c.code)}
                            className="px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50"
                        >
                            {c.name}
                        </button>
                    ))}
                </div>
            )}
            {session?.node && session.status !== 'completed' && (
                <div>
                    {session.node.isSafetyGate && (
                        <p className="text-xs uppercase tracking-wide text-red-700 mb-1">Safety</p>
                    )}
                    {session.node.promptAgent && (
                        <p className="text-sm font-medium text-gray-900 mb-1">{session.node.promptAgent || session.node.prompt}</p>
                    )}
                    <p className="text-sm text-gray-800 mb-1">{session.node.prompt}</p>
                    {session.node.internalNote && (
                        <p className="text-xs text-gray-500 mb-2">{session.node.internalNote}</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                        {(session.node.options || []).map((o) => (
                            <button
                                key={o.value}
                                type="button"
                                disabled={busy}
                                onClick={() => answer(o.value)}
                                className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded-md"
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {session?.outcome && (
                <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                    <p className="font-medium">{session.outcome.type}</p>
                    <p>{session.outcome.advice}</p>
                    {session.outcome.serviceTypeName && (
                        <p className="text-xs text-gray-600 mt-1">
                            {session.outcome.serviceTypeName}
                            {session.outcome.subtypeName ? ` · ${session.outcome.subtypeName}` : ''}
                            {session.outcome.causeName ? ` · ${session.outcome.causeName}` : ''}
                        </p>
                    )}
                </div>
            )}
            {path.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                    {path.filter((p) => !p.autoAnswered).map((p) => `${p.code}:${p.value}`).join(' → ')}
                </p>
            )}
        </div>
    );
};

export default TriagePanel;
